import React, { useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogActions, Button, useTheme, useMediaQuery, DialogContent, Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Query } from 'system/query/Query';
import { QueryInfo } from 'system/query/QueryInfo';
import APIRequest from 'system/APIRequest';
import SelectedQueries from './SelectedQueries';
import QueryList from 'routes/queryManagement/QueryList';

export interface SavedQueryDialogProps {
  isOpen: boolean;

  allowMultipleSelection?: boolean;

  onClose: () => void;

  onSubmit?: (queries: Query[]) => void;
  onInsert?: (queries: Query[]) => void;
  onReplace?: (queries: Query[]) => void;

  showSelectAllButton?: boolean;
  maximumSelectedQueries?: number;
  onSubmitAll?: (queries: Query[]) => void;

  // Optionally restrict query list to a specific dataset
  fixedDatasetId?: string;
}

export default function SavedQueryDialog(props: SavedQueryDialogProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedQueries, setSelectedQueries] = useState<Query[] | undefined>();
  const [datasetQueryInfo, setDatasetQueryInfo] = useState<QueryInfo | undefined>();
  const [availableQueries, setAvailableQueries] = useState<Query[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props.fixedDatasetId) {
      setDatasetQueryInfo(undefined);
      return;
    }

    APIRequest(`query-info/${props.fixedDatasetId}`, {}, {
      method: 'GET',
    }).then((response) => {
      setDatasetQueryInfo(response as QueryInfo);
    });
  }, [props.fixedDatasetId]);

  const datasetLabel = datasetQueryInfo ? ` ${i18n.t('query-form.using-dataset')}: ${datasetQueryInfo?.dataset?.name}` : '';

  let maxAddableQueries = (props.maximumSelectedQueries || availableQueries.length) - (selectedQueries?.length || 0);
  if (maxAddableQueries > availableQueries.length) {
    maxAddableQueries = availableQueries.length;
  }

  const canSelectMulti = maxAddableQueries > 0 && props.allowMultipleSelection;

return (
  <Dialog
    open={props.isOpen}
    onClose={props.onClose}
    fullScreen={isSmallScreen}
    fullWidth={true}
    maxWidth="md"
    sx={{
      margin:{xs:0, sm:0},
    }} >
    <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="h4">
        {`${i18n.t('query-form.saved-queries')}${datasetLabel}`}
      </Typography>
    </DialogTitle>
    <DialogContent ref={scrollRef} sx={{ backgroundColor: theme.palette.background.default, p: 0 }} >
      <Box sx={{
        p: 1
      }}>
        <QueryList
          multipleSelect={true}
          onQueriesLoaded={(queries) => setAvailableQueries(queries)}
          onSelectQueries={(queries) => setSelectedQueries(queries)}
          persistSelection={true}
          scrollRef={scrollRef}
          selectedQueries={selectedQueries}
        />
      </Box>
    </DialogContent>
    <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', borderTop: 1,  borderColor: 'divider' }}>
      {(selectedQueries && selectedQueries.length > 0) &&
        <Box
          sx={{
            zIndex: 1000,
            width: '100%',
            flexBasis: '100%',
            display: 'block',
            p: 1, pt: 0, bottom: 0 }}
          onClick={(event) => {
            // Prevent bubbling clicks
            event.stopPropagation();
            event.preventDefault();
          }}
          >
        <span style={{display: "inline-block", paddingTop: "8px", verticalAlign: "middle"}}>
          {i18n.t('widgets.selected-queries')}:
        </span>
        <SelectedQueries
          selectedQueries={selectedQueries || []}
          removeSelectedQuery={(queryId: string) => {
            if (selectedQueries && setSelectedQueries) {
              setSelectedQueries(selectedQueries.filter((query) => query.id !== queryId));
            }
          }} />
        </Box>
      }
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 1 }}>
      {(canSelectMulti && props.showSelectAllButton && availableQueries?.length > 0) && (
        <Button
          variant="text"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const selectedQueryIds = selectedQueries?.map((query) => query.id) || [];
            const unselectedAvailableQueries = availableQueries.filter((query) => {
              return !selectedQueryIds.includes(query.id);
            });
            const queriesToAdd = unselectedAvailableQueries.slice(0, maxAddableQueries);
            const newSelectedQueries = [...(selectedQueries || []), ...queriesToAdd];
            setSelectedQueries(newSelectedQueries);
          }}>
          {i18n.t('query-form.add-up-to-count', { count: maxAddableQueries })}
        </Button>
      ) }
      <Button
        variant="outlined"
        onClick={props.onClose}>
        {i18n.t('cancel')}
      </Button>
      { props.onReplace &&
        <Button
          disabled={!selectedQueries || selectedQueries.length === 0}
          variant="contained"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (props.onReplace) {
              props.onReplace(selectedQueries || []);
            }
          }}>
          {i18n.t('replace')}
        </Button>
      }
      { props.onInsert &&
        <Button
          disabled={!selectedQueries || selectedQueries.length === 0}
          variant="contained"
          color="success"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (props.onInsert) {
              props.onInsert(selectedQueries || []);
            }
          }}>
          {i18n.t('insert')}
        </Button>
      }
      { !(props.onInsert || props.onReplace) && (
        <Button
          disabled={!selectedQueries || selectedQueries.length === 0}
          variant="contained"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (props.onSubmit) {
              props.onSubmit(selectedQueries || []);
            }
          }}>
          {props.allowMultipleSelection && selectedQueries && selectedQueries.length > 1 && <>
            {i18n.t('query-form.submit-selected-count', { count: selectedQueries.length })}
          </>}
          {(!props.allowMultipleSelection || !selectedQueries || selectedQueries.length < 2) && <>
            {i18n.t('submit')}
          </>}
        </Button>
      )}
      </Box>
    </DialogActions>
  </Dialog>
);
}
